/**
 * TemplateImportV2Modal — ZIP 기반 v2 파이프라인 업로드 모달.
 * POST /api/admin-ai/templates/import 에 multipart + SSE 로 전송.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Spinner } from '../ui/Spinner'
import { Toggle } from '../ui/Toggle'
import { initStepwiseImport, type StepwiseSession } from './template-import/useStepwiseImport'

interface Props {
  open: boolean
  onClose: () => void
  /** 성공 시 호출 — 템플릿 목록 refetch 용. */
  onSuccess?: (templateId: string) => void
  /** 초기 모드. 기본 'auto'. Templates 페이지의 어떤 버튼으로 열렸는지 반영. */
  initialMode?: 'auto' | 'stepwise'
}

type Phase = 'idle' | 'uploading' | 'done' | 'error'

interface SseEvent {
  type: 'log' | 'done' | 'error'
  message?: string
  error?: string
  conversionId?: string
  templateId?: string
  status?: 'SUCCESS' | 'FAILED'
  errors?: Array<{ stage: string; code?: string; message: string }>
}

function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

function fileNameToName(fileName: string): string {
  return fileName.replace(/\.zip$/i, '').replace(/[_-]+/g, ' ').trim()
}

export function TemplateImportV2Modal({ open, onClose, onSuccess, initialMode = 'auto' }: Props) {
  const navigate = useNavigate()
  const [file, setFile] = useState<File | null>(null)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [skipLlm, setSkipLlm] = useState(true)
  const [stepwiseMode, setStepwiseMode] = useState(initialMode === 'stepwise')
  const [phase, setPhase] = useState<Phase>('idle')
  const [logs, setLogs] = useState<string[]>([])
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [result, setResult] = useState<{ templateId?: string; conversionId?: string; status?: string; errors?: SseEvent['errors'] } | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const logEndRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) {
      setFile(null); setName(''); setSlug(''); setLogs([]); setErrorMsg(null); setResult(null); setPhase('idle')
      abortRef.current?.abort()
    } else {
      setStepwiseMode(initialMode === 'stepwise')
    }
  }, [open, initialMode])

  useEffect(() => { logEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [logs.length])

  const handleFile = useCallback((f: File) => {
    if (!f.name.toLowerCase().endsWith('.zip')) { setErrorMsg('ZIP 파일만 업로드 가능합니다'); return }
    setFile(f)
    if (!name) {
      const n = fileNameToName(f.name)
      setName(n)
      setSlug(slugify(n))
    }
  }, [name])

  const handleStepwiseStart = useCallback(async () => {
    if (!file || !name.trim() || !slug.trim()) { setErrorMsg('파일·이름·slug 모두 필수'); return }
    setPhase('uploading'); setErrorMsg(null)
    try {
      const init = await initStepwiseImport({ file, name: name.trim(), slug: slug.trim(), skipLlm })
      const session: StepwiseSession = {
        sessionId: init.sessionId,
        zipS3Key: init.zipS3Key,
        name: init.name,
        slug: init.slug,
        skipLlm: init.skipLlm,
        llmModel: init.llmModel,
        initialStage: 'extract',
      }
      onClose()
      navigate('/wedding/templates/import/stepwise', { state: session })
    } catch (e) {
      const m = e instanceof Error ? e.message : String(e)
      setErrorMsg(m); setPhase('error')
    }
  }, [file, name, slug, skipLlm, navigate, onClose])

  const handleSubmit = useCallback(async () => {
    if (!file || !name.trim() || !slug.trim()) { setErrorMsg('파일·이름·slug 모두 필수'); return }
    setPhase('uploading'); setLogs([]); setErrorMsg(null); setResult(null)

    const fd = new FormData()
    fd.set('file', file, file.name)
    fd.set('name', name.trim())
    fd.set('slug', slug.trim())
    fd.set('skipLlm', String(skipLlm))

    const ctrl = new AbortController()
    abortRef.current = ctrl

    try {
      const res = await fetch('/api/admin/templates/import', {
        method: 'POST',
        body: fd,
        credentials: 'include',
        signal: ctrl.signal,
      })
      if (!res.ok && res.status !== 200) {
        const body = await res.json().catch(() => ({ error: { message: res.statusText } }))
        setErrorMsg(body?.error?.message ?? `업로드 실패 (${res.status})`)
        setPhase('error')
        return
      }
      const reader = res.body?.getReader()
      if (!reader) { setErrorMsg('SSE 스트림 없음'); setPhase('error'); return }
      const decoder = new TextDecoder()
      let buf = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        const lines = buf.split('\n')
        buf = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const ev = JSON.parse(line.slice(6)) as SseEvent
            if (ev.type === 'log' && ev.message) setLogs((p) => [...p, ev.message!])
            else if (ev.type === 'done') {
              setResult({ templateId: ev.templateId, conversionId: ev.conversionId, status: ev.status, errors: ev.errors })
              setPhase(ev.status === 'SUCCESS' ? 'done' : 'error')
              if (ev.status === 'SUCCESS' && ev.templateId) onSuccess?.(ev.templateId)
            } else if (ev.type === 'error') {
              setErrorMsg(ev.error ?? 'Unknown error'); setPhase('error')
            }
          } catch { /* noop */ }
        }
      }
    } catch (e) {
      const m = e instanceof Error ? e.message : String(e)
      if (!/AbortError/.test(m)) { setErrorMsg(m); setPhase('error') }
    }
  }, [file, name, slug, skipLlm, onSuccess])

  const goToTemplate = useCallback(() => {
    if (result?.templateId) { navigate(`/wedding/templates/${result.templateId}`); onClose() }
  }, [result?.templateId, navigate, onClose])

  const busy = phase === 'uploading'

  return (
    <Modal open={open} onClose={busy ? () => undefined : onClose} title="템플릿 ZIP 가져오기 (v2)" size="lg">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500 }}>ZIP 파일</label>
          <input
            type="file"
            accept=".zip,application/zip"
            disabled={busy}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
          />
          {file && <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</div>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500 }}>이름</label>
            <Input value={name} disabled={busy} onChange={(e) => { setName(e.target.value); if (!slug) setSlug(slugify(e.target.value)) }} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500 }}>slug</label>
            <Input value={slug} disabled={busy} onChange={(e) => setSlug(slugify(e.target.value))} />
          </div>
        </div>

        <Toggle
          label="LLM 바인딩 패스 건너뛰기 (마커만으로 충분한 경우)"
          checked={skipLlm}
          disabled={busy}
          onChange={(next) => setSkipLlm(next)}
        />

        <Toggle
          label="단계별 모드 — 각 stage 결과를 UI 에서 검토·편집·재실행 (세션 전용)"
          checked={stepwiseMode}
          disabled={busy}
          onChange={(next) => setStepwiseMode(next)}
        />

        {errorMsg && <div style={{ padding: 10, background: '#fee', color: '#900', borderRadius: 6, fontSize: 13 }}>{errorMsg}</div>}

        {(logs.length > 0 || busy) && (
          <div style={{ maxHeight: 260, overflowY: 'auto', background: '#111', color: '#eee', padding: 12, borderRadius: 6, fontSize: 12, fontFamily: 'monospace' }}>
            {logs.map((l, i) => (<div key={i}>{l}</div>))}
            {busy && <div style={{ opacity: 0.6 }}>···</div>}
            <div ref={logEndRef} />
          </div>
        )}

        {result?.status === 'FAILED' && result.errors && (
          <div style={{ padding: 10, background: '#fff5e6', border: '1px solid #ffb84d', borderRadius: 6, fontSize: 13 }}>
            <strong>변환 실패:</strong>
            <ul style={{ margin: '6px 0 0 16px' }}>
              {result.errors.map((e, i) => (
                <li key={i}>[{e.stage}{e.code ? `/${e.code}` : ''}] {e.message}</li>
              ))}
            </ul>
            {file && (
              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={async () => {
                    // 실패 지점을 단계별 UI 에서 직접 검증하고 싶을 때.
                    // 자동 모드가 이미 slug 을 사용했으므로 임시 suffix 를 붙여 새 세션 시작.
                    const debugSlug = `${slug.trim()}-step-${Math.random().toString(36).slice(2, 7)}`
                    setPhase('uploading'); setErrorMsg(null)
                    try {
                      const init = await initStepwiseImport({ file, name: name.trim(), slug: debugSlug, skipLlm })
                      const session: StepwiseSession = {
                        sessionId: init.sessionId,
                        zipS3Key: init.zipS3Key,
                        name: init.name,
                        slug: init.slug,
                        skipLlm: init.skipLlm,
                        llmModel: init.llmModel,
                        initialStage: 'extract',
                      }
                      onClose()
                      navigate('/wedding/templates/import/stepwise', { state: session })
                    } catch (e) {
                      const m = e instanceof Error ? e.message : String(e)
                      setErrorMsg(m); setPhase('error')
                    }
                  }}
                >
                  단계별 모드로 재분석
                </Button>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {phase === 'done' && <Button variant="primary" onClick={goToTemplate}>템플릿 열기</Button>}
          {phase !== 'done' && <Button variant="outline" onClick={onClose} disabled={busy}>닫기</Button>}
          {phase !== 'done' && (
            <Button
              variant="primary"
              onClick={stepwiseMode ? handleStepwiseStart : handleSubmit}
              disabled={!file || !name || !slug || busy}
            >
              {busy ? (
                <><Spinner size="sm" /> {stepwiseMode ? '세션 준비 중...' : '업로드 중...'}</>
              ) : stepwiseMode ? (
                '단계별 모드 시작'
              ) : (
                '업로드 + 변환'
              )}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
